import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Flex, Box, Text, Card, Button as RadixButton } from '@radix-ui/themes';
import { CheckIcon, CrossCircledIcon, ReloadIcon, CalendarIcon, ClockIcon } from '@radix-ui/react-icons';
import { patientAPI } from '../../utils/api/directus';
import Button from '../../components/common/Button';
import styles from './styles.module.scss';

const AppointmentConfirm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const appointmentId = searchParams.get('id');
  const token = searchParams.get('token');

  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchAppointment = async () => {
      if (!appointmentId) {
        setError('Invalid confirmation link. Please check your message.');
        setLoading(false);
        return;
      }

      try {
        const data = await patientAPI.getAppointmentById(appointmentId, token);
        setAppointment(data);
      } catch (err) {
        setError(err.message || 'Failed to load appointment details.');
      } finally {
        setLoading(false);
      }
    };

    fetchAppointment();
  }, [appointmentId, token]);

  const handleConfirm = async () => {
    if (!appointmentId) return;

    setConfirming(true);
    setError(null);

    try {
      await patientAPI.confirmAppointment(appointmentId, token);
      setSuccess(true);
      
      // Redirect to dashboard after 3 seconds
      setTimeout(() => {
        navigate('/patient-dashboard');
      }, 3000);
    } catch (err) {
      setError(err.message || 'Failed to confirm appointment. Please try again.');
      setConfirming(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getServiceName = (service) => {
    if (!service) return 'General consultation';
    return service.name || service.Name || 'General consultation';
  };

  const getDoctorName = (doctor) => {
    if (!doctor) return 'Staff';
    return doctor.name || `${doctor.first_name || ''} ${doctor.last_name || ''}`.trim() || 'Staff';
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <Card className={styles.card}>
          <Flex direction="column" align="center" gap="4">
            <ReloadIcon className={styles.spinner} />
            <Text size="4" className={styles.loadingText}>
              Loading appointment details...
            </Text>
          </Flex>
        </Card>
      </div>
    );
  }

  if (error && !appointment) {
    return (
      <div className={styles.container}>
        <Card className={styles.card}>
          <Flex direction="column" align="center" gap="4">
            <CrossCircledIcon className={styles.errorIcon} />
            <Text size="5" weight="bold" color="red">
              Error
            </Text>
            <Text size="3" color="red" className={styles.errorText}>
              {error}
            </Text>
            <RadixButton variant="soft" onClick={() => navigate('/login')}>
              Go to Login
            </RadixButton>
          </Flex>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className={styles.container}>
        <Card className={styles.successCard}>
          <Flex direction="column" align="center" gap="4">
            <div className={styles.successIconWrapper}>
              <CheckIcon className={styles.successIcon} />
            </div>
            <Text size="6" weight="bold" className={styles.successTitle}>
              Appointment Confirmed!
            </Text>
            <Text size="3" className={styles.successText}>
              Your appointment has been confirmed. You will be redirected to your dashboard shortly.
            </Text>
          </Flex>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        <Flex direction="column" gap="5">
          <div className={styles.header}>
            <Text size="7" weight="bold" className={styles.title}>
              Confirm Your Appointment
            </Text>
            <Text size="3" className={styles.subtitle}>
              Please review the details below and confirm your appointment
            </Text>
          </div>

          {appointment && (
            <div className={styles.appointmentDetails}>
              <Box className={styles.detailBox}>
                <Text size="4" weight="bold" className={styles.detailLabel}>
                  Service
                </Text>
                <Text size="5" className={styles.detailValue}>
                  {getServiceName(appointment.service_id)}
                </Text>
              </Box>

              <Box className={styles.detailBox}>
                <Text size="4" weight="bold" className={styles.detailLabel}>
                  Doctor
                </Text>
                <Text size="5" className={styles.detailValue}>
                  Dr. {getDoctorName(appointment.doctor_id)}
                </Text>
              </Box>

              <Flex gap="4" wrap="wrap">
                <Box className={styles.detailBox}>
                  <Flex align="center" gap="2" className={styles.detailRow}>
                    <CalendarIcon className={styles.detailIcon} />
                    <div>
                      <Text size="2" className={styles.detailLabel}>
                        Date
                      </Text>
                      <Text size="4" weight="medium" className={styles.detailValue}>
                        {formatDate(appointment.appointment_date)}
                      </Text>
                    </div>
                  </Flex>
                </Box>

                <Box className={styles.detailBox}>
                  <Flex align="center" gap="2" className={styles.detailRow}>
                    <ClockIcon className={styles.detailIcon} />
                    <div>
                      <Text size="2" className={styles.detailLabel}>
                        Time
                      </Text>
                      <Text size="4" weight="medium" className={styles.detailValue}>
                        {formatTime(appointment.appointment_date)}
                      </Text>
                    </div>
                  </Flex>
                </Box>
              </Flex>

              {appointment.notes && (
                <Box className={styles.notesBox}>
                  <Text size="2" weight="bold" className={styles.notesLabel}>
                    Notes
                  </Text>
                  <Text size="3" className={styles.notesText}>
                    {appointment.notes}
                  </Text>
                </Box>
              )}
            </div>
          )}

          {error && (
            <Box className={styles.errorBox}>
              <Text size="2" color="red" weight="medium">
                {error}
              </Text>
            </Box>
          )}

          <Flex gap="3" justify="center">
            <Button
              variant="normal"
              size="4"
              onClick={handleConfirm}
              disabled={confirming || !appointment}
              backgroundColor="var(--color-primary)"
              textColor="var(--color-secondary)"
              className={styles.confirmButton}
            >
              {confirming ? (
                <>
                  <ReloadIcon className={styles.buttonSpinner} />
                  Confirming...
                </>
              ) : (
                <>
                  <CheckIcon />
                  Confirm Appointment
                </>
              )}
            </Button>
          </Flex>
        </Flex>
      </Card>
    </div>
  );
};

export default AppointmentConfirm;

